import type { HEVCFrame, HEVCStreamInfo, DecodeResult, DecoderOptions, SuppressedPicture } from "./types.js";
import { parseFrameLayout, type FrameLayout } from "./frame-layout.js";

/**
 * Emscripten module interface (subset we use)
 */
interface EmscriptenModule {
  cwrap(name: string, returnType: string | null, argTypes: string[]): Function;
  _malloc(size: number): number;
  _free(ptr: number): void;
  getValue(ptr: number, type: string): number;
  HEAPU8: Uint8Array;
  HEAPU16: Uint16Array;
}

interface DecoderAPI {
  create: () => number;
  destroy: (dec: number) => void;
  decode: (dec: number, ptr: number, size: number) => number;
  getFrameCount: (dec: number) => number;
  getFrame: (dec: number, index: number, framePtr: number) => number;
  getInfo: (dec: number, infoPtr: number) => number;
  feed: (dec: number, ptr: number, size: number) => number;
  drain: (dec: number, countPtr: number) => number;
  getDrainedFrame: (dec: number, index: number, framePtr: number) => number;
  flush: (dec: number) => number;
  getSuppressedPictureCount: (dec: number) => number;
  takeSuppressedPictures: (dec: number, out: number, max: number) => number;
  frameLayout: () => string;
}

/**
 * HEVC/H.265 Decoder — JavaScript wrapper for the WASM module.
 *
 * @example
 * ```ts
 * const decoder = await HEVCDecoder.create();
 * const { frames, info } = decoder.decode(bitstreamBytes);
 * console.log(`${info.width}x${info.height}, ${frames.length} frames`);
 * decoder.destroy();
 * ```
 */
export class HEVCDecoder {
  private _m: EmscriptenModule;
  private _api: DecoderAPI;
  private _dec: number;
  /** Where HEVCFrame's fields sit, as the module itself reports them. */
  private _layout: FrameLayout;

  private constructor(module: EmscriptenModule) {
    this._m = module;
    this._api = {
      create: module.cwrap("hevc_decoder_create", "number", []) as () => number,
      destroy: module.cwrap("hevc_decoder_destroy", null, ["number"]) as (dec: number) => void,
      decode: module.cwrap("hevc_decoder_decode", "number", ["number", "number", "number"]) as (dec: number, ptr: number, size: number) => number,
      getFrameCount: module.cwrap("hevc_decoder_get_frame_count", "number", ["number"]) as (dec: number) => number,
      getFrame: module.cwrap("hevc_decoder_get_frame", "number", ["number", "number", "number"]) as (dec: number, index: number, framePtr: number) => number,
      getInfo: module.cwrap("hevc_decoder_get_info", "number", ["number", "number"]) as (dec: number, infoPtr: number) => number,
      feed: module.cwrap("hevc_decoder_feed", "number", ["number", "number", "number"]) as (dec: number, ptr: number, size: number) => number,
      drain: module.cwrap("hevc_decoder_drain", "number", ["number", "number"]) as (dec: number, countPtr: number) => number,
      getDrainedFrame: module.cwrap("hevc_decoder_get_drained_frame", "number", ["number", "number", "number"]) as (dec: number, index: number, framePtr: number) => number,
      flush: module.cwrap("hevc_decoder_flush", "number", ["number"]) as (dec: number) => number,
      getSuppressedPictureCount: module.cwrap("hevc_decoder_get_suppressed_picture_count", "number", ["number"]) as (dec: number) => number,
      takeSuppressedPictures: module.cwrap("hevc_decoder_take_suppressed_pictures", "number", ["number", "number", "number"]) as (dec: number, out: number, max: number) => number,
      frameLayout: module.cwrap("hevc_frame_layout", "string", []) as () => string,
    };
    this._layout = parseFrameLayout(this._api.frameLayout());
    this._dec = this._api.create();
    if (!this._dec) throw new Error("Failed to create HEVC decoder");
  }

  /**
   * Create a new decoder instance. Loads the WASM module.
   */
  static async create(options?: DecoderOptions): Promise<HEVCDecoder> {
    const factoryOpts: Record<string, unknown> = {};
    if (options?.wasmBinaryUrl) {
      factoryOpts.locateFile = () => options.wasmBinaryUrl;
    }

    // 1. Check for globally loaded Emscripten module (via <script> tag)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = globalThis as any;
    if (typeof g.HEVCDecoderModule === "function") {
      const module = await g.HEVCDecoderModule(factoryOpts) as EmscriptenModule;
      return new HEVCDecoder(module);
    }

    // 2. Try dynamic import (works with bundlers like Vite/Webpack)
    const wasmUrl = options?.wasmUrl ?? "./wasm/hevc-decode.js";
    const mod = await import(/* @vite-ignore */ wasmUrl);
    const fn = mod.default ?? mod;
    const module = await (fn(factoryOpts) as Promise<EmscriptenModule>);
    return new HEVCDecoder(module);
  }

  /**
   * Decode a complete HEVC bitstream.
   * @param data Raw .265 bitstream bytes
   */
  decode(data: Uint8Array): DecodeResult {
    const m = this._m;
    const ptr = m._malloc(data.length);
    try {
      m.HEAPU8.set(data, ptr);
      const ret = this._api.decode(this._dec, ptr, data.length);
      if (ret !== 0) throw new Error(`Decode failed (code ${ret})`);

      const count = this._api.getFrameCount(this._dec);
      const frames: HEVCFrame[] = [];
      for (let i = 0; i < count; i++) {
        const frame = this._extractFrame(i);
        if (frame) frames.push(frame);
      }

      const info = this._extractInfo();
      return { frames, info };
    } finally {
      m._free(ptr);
    }
  }

  /** Number of decoded frames available */
  get frameCount(): number {
    return this._api.getFrameCount(this._dec);
  }

  /** Get stream info (available after decode) */
  get info(): HEVCStreamInfo | null {
    return this._extractInfo();
  }

  private _extractFrame(index: number): HEVCFrame | null {
    const m = this._m;
    const framePtr = m._malloc(this._layout.size);
    try {
      const ret = this._api.getFrame(this._dec, index, framePtr);
      if (ret !== 0) return null;
      return this._readFrameFromPtr(framePtr);
    } finally {
      m._free(framePtr);
    }
  }

  private _extractDrainedFrame(index: number): HEVCFrame | null {
    const m = this._m;
    const framePtr = m._malloc(this._layout.size);
    try {
      const ret = this._api.getDrainedFrame(this._dec, index, framePtr);
      if (ret !== 0) return null;
      return this._readFrameFromPtr(framePtr);
    } finally {
      m._free(framePtr);
    }
  }

  private _readFrameFromPtr(framePtr: number): HEVCFrame {
    const m = this._m;
    const at = this._layout.offset;
    const yPtr    = m.getValue(framePtr + at.y, "*");
    const cbPtr   = m.getValue(framePtr + at.cb, "*");
    const crPtr   = m.getValue(framePtr + at.cr, "*");
    const width   = m.getValue(framePtr + at.width, "i32");
    const height  = m.getValue(framePtr + at.height, "i32");
    const strideY = m.getValue(framePtr + at.stride_y, "i32");
    const strideC = m.getValue(framePtr + at.stride_c, "i32");
    const cw      = m.getValue(framePtr + at.chroma_width, "i32");
    const ch      = m.getValue(framePtr + at.chroma_height, "i32");
    const bd      = m.getValue(framePtr + at.bit_depth, "i32");
    const poc     = m.getValue(framePtr + at.poc, "i32");
    const cvsId   = m.getValue(framePtr + at.cvs_id, "i32");

    const bps     = m.getValue(framePtr + at.bytes_per_sample, "i32");

    const y  = copyPlane(m, yPtr, width, height, strideY, bps);
    const cb = copyPlane(m, cbPtr, cw, ch, strideC, bps);
    const cr = copyPlane(m, crPtr, cw, ch, strideC, bps);

    return { y, cb, cr, width, height, chromaWidth: cw, chromaHeight: ch, bitDepth: bd, poc, cvsId };
  }

  private _extractInfo(): HEVCStreamInfo | null {
    const m = this._m;
    const infoPtr = m._malloc(24);
    try {
      const ret = this._api.getInfo(this._dec, infoPtr);
      if (ret !== 0) return null;
      return {
        width:        m.getValue(infoPtr, "i32"),
        height:       m.getValue(infoPtr + 4, "i32"),
        bitDepth:     m.getValue(infoPtr + 8, "i32"),
        chromaFormat: m.getValue(infoPtr + 12, "i32"),
        profile:      m.getValue(infoPtr + 16, "i32"),
        level:        m.getValue(infoPtr + 20, "i32"),
      };
    } finally {
      m._free(infoPtr);
    }
  }

  // --- Incremental API (streaming) ---

  /**
   * Feed a chunk of data containing one or more complete NAL units.
   * The decoder accumulates parameter sets and decodes pictures incrementally.
   * Call drain() after each feed() to retrieve output-ready frames.
   */
  feed(data: Uint8Array): void {
    const m = this._m;
    const ptr = m._malloc(data.length);
    try {
      m.HEAPU8.set(data, ptr);
      const ret = this._api.feed(this._dec, ptr, data.length);
      if (ret !== 0) throw new Error(`Feed failed (code ${ret})`);
    } finally {
      m._free(ptr);
    }
  }

  /**
   * Drain output-ready frames from the decoder (§C.5.2 bumping process).
   * Returns frames in display order, only when ready per DPB constraints.
   *
   * Planes are copied out of the WASM heap, so the returned frames stay
   * valid across later feed() and destroy() calls. Draining after every
   * feed() is what keeps the DPB at its bound — the decoder cannot reclaim
   * a picture before it has been bumped out. See docs/memory-envelope.md.
   */
  drain(): HEVCFrame[] {
    const m = this._m;
    const countPtr = m._malloc(4);
    try {
      const ret = this._api.drain(this._dec, countPtr);
      if (ret !== 0) return [];
      const count = m.getValue(countPtr, "i32");
      const frames: HEVCFrame[] = [];
      for (let i = 0; i < count; i++) {
        const frame = this._extractDrainedFrame(i);
        if (frame) frames.push(frame);
      }
      return frames;
    } finally {
      m._free(countPtr);
    }
  }

  /**
   * Flush all remaining frames from the DPB (call at end of stream).
   * Returns all buffered frames in display order.
   */
  flush(): HEVCFrame[] {
    const ret = this._api.flush(this._dec);
    if (ret !== 0) return [];
    // After flush, drained frames are available via getDrainedFrame
    const m = this._m;
    const countPtr = m._malloc(4);
    try {
      // flush() already populates the drained list, read the count
      // by checking how many frames are available
      const frames: HEVCFrame[] = [];
      const framePtr = m._malloc(this._layout.size);
      try {
        for (let i = 0; ; i++) {
          const r = this._api.getDrainedFrame(this._dec, i, framePtr);
          if (r !== 0) break;
          frames.push(this._readFrameFromPtr(framePtr));
        }
      } finally {
        m._free(framePtr);
      }
      return frames;
    } finally {
      m._free(countPtr);
    }
  }

  /**
   * The pictures decoded since the last call that will never be output:
   * PicOutputFlag was 0 (§C.3.1, §8.1), or a new sequence emptied them from
   * the DPB without output (§C.5.2.2). Empties the list.
   *
   * A caller that maps output frames onto per-sample timestamps needs these:
   * the sample that carried such a picture produces no frame, and without
   * knowing which one it was, every later frame of the segment takes the
   * timestamp of its predecessor. Each one comes with its CVS, since POC
   * restarts at every IRAP and alone cannot order pictures across one.
   */
  takeSuppressedPictures(): SuppressedPicture[] {
    const m = this._m;
    const count = this._api.getSuppressedPictureCount(this._dec);
    if (count <= 0) return [];

    // Two int32_t per picture: cvs_id then poc
    const ptr = m._malloc(count * 8);
    try {
      const written = this._api.takeSuppressedPictures(this._dec, ptr, count);
      if (written < 0) return [];
      const out: SuppressedPicture[] = [];
      for (let i = 0; i < written; i++) {
        out.push({
          cvsId: m.getValue(ptr + i * 8, "i32"),
          poc: m.getValue(ptr + i * 8 + 4, "i32"),
        });
      }
      return out;
    } finally {
      m._free(ptr);
    }
  }

  /** Release decoder resources */
  destroy(): void {
    if (this._dec) {
      this._api.destroy(this._dec);
      this._dec = 0;
    }
  }
}

/**
 * Copy a YUV plane out of the WASM heap, handling stride != width. 8-bit
 * pictures are stored one byte per sample (bytesPerSample 1): set() widens each
 * byte into the Uint16Array.
 */
function copyPlane(m: EmscriptenModule, ptr: number, width: number, height: number, stride: number,
                   bytesPerSample: number): Uint16Array {
  const out = new Uint16Array(width * height);
  const heap = bytesPerSample === 1 ? m.HEAPU8 : m.HEAPU16;
  const base = bytesPerSample === 1 ? ptr : ptr >> 1;
  for (let y = 0; y < height; y++) {
    out.set(heap.subarray(base + y * stride, base + y * stride + width), y * width);
  }
  return out;
}
